import axios, { AxiosRequestConfig } from "axios";
import { useChatStore } from '@/stores/chatStore';
import { getCachedDeviceInfo } from "./deviceInfo";
import { generateDeviceFingerprint, getCachedFingerprint } from "./fingerprint";
import { getAccessToken } from "./auth";

export interface APIResponse<T = any> {
  code: number;
  data: T;
  msg: string;
}

// 设备信息请求头键名
// const DEVICE_INFO_HEADER = "X-Device-Info";

// 创建带有设备信息的 axios 实例
export const apiClient = axios.create();

// 请求拦截器：自动添加设备信息和指纹
apiClient.interceptors.request.use(
  async (config) => {
    const deviceInfo = getCachedDeviceInfo();
    
    if (deviceInfo) {
      try {
        // 生成或获取缓存的设备指纹
        let fingerprint = getCachedFingerprint();
        if (!fingerprint) {
          fingerprint = await generateDeviceFingerprint(deviceInfo);
        }

        const accessToken = getAccessToken();
        if (accessToken) {
          config.headers['X-Access-Token'] = accessToken;
          // config.headers['X-GateWay-UserID'] = 209589;
        }
        // console.log("fingerprint", fingerprint);
        // 添加设备指纹到请求头
        config.headers["X-Device-Fingerprint"] = fingerprint;
        // config.headers["X-Device-Fingerprint"] = "1234567890";
        // config.headers["X-Device-Fingerprint"] = "b45ac39d7832a8e309e85ba5413db3ae-tc59x5";
        
        // 添加设备基本信息到请求头
        // config.headers["X-Device-Type"] = deviceInfo.deviceType;
        // config.headers["X-Session-ID"] = deviceInfo.session.sessionId;
        // config.headers["X-User-Agent"] = deviceInfo.userAgent;
        
        // 在请求参数中添加设备信息（用于 GET 请求）
        if (config.method === "get") {
          config.params = {
            ...config.params,
            // deviceType: deviceInfo.deviceType,
            // sessionId: deviceInfo.session.sessionId,
            // timestamp: deviceInfo.session.timestamp,
          };
        } else if (config.method === "post") {
          // 对于 POST 请求，将设备信息添加到请求体中
          if (config.data) {
            config.data = {
              ...config.data,
              // deviceInfo: {
              //   deviceType: deviceInfo.deviceType,
              //   sessionId: deviceInfo.session.sessionId,
              //   timestamp: deviceInfo.session.timestamp,
              //   fingerprint: fingerprint,
              // },
            };
          } else {
            config.data = {
              // deviceInfo: {
              //   deviceType: deviceInfo.deviceType,
              //   sessionId: deviceInfo.session.sessionId,
              //   timestamp: deviceInfo.session.timestamp,
              //   fingerprint: fingerprint,
              // },
            };
          }
        }
      } catch (error) {
        console.error('Failed to add fingerprint to request:', error);
        // 降级处理：使用简单的设备标识
        config.headers["X-Device-Fingerprint"] = `fallback-${deviceInfo.session.sessionId}`;
      }
    }

    return config;
  },
  (error) => {
    console.error('API request failed:', error);
    const { setServiceAvailable } = useChatStore.getState();
    setServiceAvailable(false);
    // setAbVariant("gladly");
    return Promise.reject(error);
  }
);

// 响应拦截器：处理响应
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error("API request failed:", error);
    const { setServiceAvailable } = useChatStore.getState();
    setServiceAvailable(false);
    // setAbVariant("gladly");
    return Promise.reject(error);
  }
);

// 辅助函数：创建带有设备信息的 API 请求
export async function apiRequest<T = any>(
  config: AxiosRequestConfig
): Promise<T> {
  try {
    const response = await apiClient.request<T>(config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// 辅助函数：GET 请求
export async function apiGet<T = any>(url: string, params?: any, headers?: any): Promise<T> {
  return apiRequest<T>({
    method: "get",
    url,
    params,
    headers,
  });
}

// 辅助函数：POST 请求
export async function apiPost<T = any>(url: string, data?: any, headers?: any): Promise<T> {
  return apiRequest<T>({
    method: "post",
    url,
    data,
    headers,
  });
}

// 辅助函数：PUT 请求
export async function apiPut<T = any>(url: string, data?: any, headers?: any): Promise<T> {
  return apiRequest<T>({
    method: "put",
    url,
    data,
    headers,
  });
}

// 辅助函数：DELETE 请求
export async function apiDelete<T = any>(
  url: string,
  params?: any,
  headers?: any
): Promise<T> {
  return apiRequest<T>({
    method: "delete",
    url,
    params,
    headers,
  });
}

// 导出设备信息获取函数
export { getCachedDeviceInfo } from "./deviceInfo";
